import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  useGetPricingPlansQuery,
  useDeletePricingPlanMutation,
} from "@/store/admin_api";
import { PricingPlan } from "@/model/interface/PricingPlan";
import React from "react";

function formatRate(baseRate: number, markupType: string, markupAmount: number): string {
  const markupSuffix = markupType === "PERCENTAGE" ? `${markupAmount}%` : `$${markupAmount}`;
  return `$${baseRate} + ${markupSuffix}`;
}

function PricingPlanRow({
  plan,
  onEdit,
  onDelete,
}: {
  plan: PricingPlan;
  onEdit: (plan: PricingPlan) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <TableRow className="cursor-pointer" onClick={() => onEdit(plan)}>
      <TableCell className="font-medium">
        {plan.name}
        {plan.isDefault && (
          <Badge variant="secondary" className="ml-2 text-xs">
            Default
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-xs font-mono">
        {formatRate(plan.outboundCallBaseRate, plan.outboundCallMarkupType, plan.outboundCallMarkupAmount)}
      </TableCell>
      <TableCell className="text-xs font-mono">
        {formatRate(plan.inboundCallBaseRate, plan.inboundCallMarkupType, plan.inboundCallMarkupAmount)}
      </TableCell>
      <TableCell className="text-xs font-mono">
        {formatRate(plan.outboundSmsBaseRate, plan.outboundSmsMarkupType, plan.outboundSmsMarkupAmount)}
      </TableCell>
      <TableCell className="text-xs font-mono">
        {formatRate(plan.phoneNumberBaseMonthlyRate, plan.phoneNumberMarkupType, plan.phoneNumberMarkupAmount)}
      </TableCell>
      <TableCell>
        {plan.isDefault ? <Badge>Default</Badge> : <Badge variant="outline">Custom</Badge>}
      </TableCell>
      <TableCell>
        {!plan.isDefault && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              onDelete(plan.id);
            }}
          >
            Delete
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function PricingPlansAdmin() {
  const navigate = useNavigate();
  const { data: plans, isLoading } = useGetPricingPlansQuery();
  const [deletePlan] = useDeletePricingPlanMutation();

  const openCreate = () => navigate("/platform-admin/pricing/plans/new");
  const openEdit = (plan: PricingPlan) => navigate(`/platform-admin/pricing/plans/${plan.id}`);

  const handleDelete = async (id: number) => {
    try {
      await deletePlan(id).unwrap();
      toast.success("Pricing plan deleted");
    } catch (deleteError) {
      const apiError = deleteError as { data?: { detail?: string } };
      toast.error(apiError?.data?.detail || "Failed to delete");
    }
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Pricing Plans</h3>
          <p className="text-sm text-muted-foreground">Base rates and markup configuration for usage-based billing.</p>
        </div>
        <Button onClick={openCreate}>New Plan</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Outbound Call</TableHead>
            <TableHead>Inbound Call</TableHead>
            <TableHead>Outbound SMS</TableHead>
            <TableHead>Phone #</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans?.map((plan) => (
            <PricingPlanRow key={plan.id} plan={plan} onEdit={openEdit} onDelete={handleDelete} />
          ))}
          {(!plans || plans.length === 0) && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">
                No pricing plans
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
